import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UserRound, Loader2, ShieldCheck } from "lucide-react";

// A member owes us a real first + last name before they can list / buy / message. Individuals
// only (businesses transact under their legal business name; ID-verified members already have a
// Stripe legal name). Skippable — browsing stays open — but the gated actions keep prompting
// (the backend enforces with a 428 that also re-opens this via the "os:name-required" event).
function needsName(user) {
  if (!user || user === true) return false;
  if ((user.account_type || "individual") === "business") return false;
  if (user.identity_verified) return false;
  return !((user.first_name || "").trim() || (user.last_name || "").trim());
}

export default function NameGate() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Auto-prompt once per session when the account still owes a name ("prompted on next login").
  useEffect(() => {
    if (!needsName(user)) return;
    if (sessionStorage.getItem("os_name_prompt_seen") === "1") return;
    sessionStorage.setItem("os_name_prompt_seen", "1");
    setOpen(true);
  }, [user]);

  // A gated action (list/buy/message) hit the 428 gate → open the prompt.
  useEffect(() => {
    const onNeed = () => { if (needsName(user)) setOpen(true); };
    window.addEventListener("os:name-required", onNeed);
    return () => window.removeEventListener("os:name-required", onNeed);
  }, [user]);

  if (!needsName(user)) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (first.trim().length < 2 || last.trim().length < 2) { setErr("Enter your first and last name."); return; }
    setBusy(true);
    try {
      await api.put("/me/profile", { first_name: first.trim(), last_name: last.trim() });
      await refresh();
      toast.success("Thanks — your name is saved.");
      setOpen(false);
    } catch (e2) {
      setErr(formatError(e2.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const inputCls = "mt-1 w-full bg-[#0D0E0F] border border-[#3E3F42] focus:border-[#57682A] outline-none px-3 py-2.5 text-[#E8E8EA] placeholder:text-[#6B6D70] font-body transition-colors";
  const labelCls = "font-mono text-xs uppercase tracking-wide text-[#8B8D90]";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-surface border-sniper text-foreground max-w-md" data-testid="name-gate-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2">
            <UserRound className="w-5 h-5 text-olive-bright" /> Add your name
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm font-body text-secondary-foreground">
          OpticSwap is a verified-only marketplace. Add your real first and last name to list, buy or message. Browsing stays open either way.
        </p>
        <form onSubmit={submit} className="mt-3 space-y-3">
          {err && <div data-testid="name-gate-error" className="border border-blaze/50 bg-blaze/10 text-blaze text-sm px-3 py-2 font-mono">{err}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>First name</label>
              <input data-testid="name-gate-first" value={first} onChange={(e) => setFirst(e.target.value)} autoFocus autoComplete="given-name" placeholder="e.g. Jordan" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Last name</label>
              <input data-testid="name-gate-last" value={last} onChange={(e) => setLast(e.target.value)} autoComplete="family-name" placeholder="e.g. Reyes" className={inputCls} />
            </div>
          </div>
          <p className="flex items-start gap-1.5 text-xs text-[#6B6D70] font-body leading-relaxed">
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-olive/70" /> Private — never shown publicly. Buyers see only your @handle.
          </p>
          <div className="flex items-center gap-2 pt-1">
            <button type="submit" disabled={busy} data-testid="name-gate-submit"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-sm py-2.5 transition-colors">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save name
            </button>
            <button type="button" onClick={() => setOpen(false)} data-testid="name-gate-later"
              className="px-4 py-2.5 font-mono uppercase text-xs text-secondary-foreground hover:text-foreground transition-colors">
              Later
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
